/**
 * External activities — a step the **host** opens, because it lives outside the asset (D78).
 *
 * The first is Focus-FreeTalkBot's speaking practice: a spoken roleplay the bot draws and voices
 * itself. An asset can never reach it — no endpoint, no credential (invariant 1) — so the asset only
 * asks: "open this scenario", and the host opens it and says when it is finished. Nothing comes back
 * but that: no transcript, no mark (invariant 2).
 *
 * A step names the talk one of two ways, exactly one (the bot's `scenario/1`, its spec 025 US8):
 * a scenario in the bot's catalog, by key; or the scenario itself — the author's content: who the
 * partner is, the situation, the goals and words, the opening line, and a persona the bot reads but
 * never shows. Either way, who the learner is reaches the bot from the host, as a pass the platform
 * signs (Focus's `#ftlaunch` assertion), never through the asset (invariant 3).
 */

/** The external activities a host may offer. A new one is an entry here and an adapter in the host. */
export const EXTERNAL_SERVICES = ['free-talk'] as const;
export type ExternalService = (typeof EXTERNAL_SERVICES)[number];

/** The bot's levels and voices (its scenario/1). */
export const FREE_TALK_LEVELS = ['Pre-A1', 'A1', 'A2', 'B1'] as const;
export const FREE_TALK_VOICES = ['alloy', 'ash', 'ballad', 'cedar', 'coral', 'echo', 'marin', 'sage', 'shimmer', 'verse'] as const;
export const FREE_TALK_TYPES = ['roleplay', 'goal_driven'] as const;

/** A scenario in the bot's catalog, by its key. */
export interface FreeTalkScenarioKey {
  readonly scenarioId: string;
}

/** The scenario itself, as the bot's `scenario/1` names it — the author's content. */
export interface FreeTalkScenario {
  readonly scenarioId: string;
  readonly type: (typeof FREE_TALK_TYPES)[number];
  readonly title: string;
  readonly level: (typeof FREE_TALK_LEVELS)[number];
  readonly character: { readonly name: string; readonly role: string; readonly voice: (typeof FREE_TALK_VOICES)[number] };
  readonly openingLine: string;
  readonly situation: string;
  readonly goals: readonly string[];
  readonly words: readonly string[];
  /** For the bot only — the character's personality; never shown, any script. */
  readonly persona?: string;
  /** The curriculum can-do statements the talk practises, up to 3, each with its code ("CD-A2-SI-IE-02"). */
  readonly canDo?: readonly FreeTalkCanDo[];
  /**
   * The sentence patterns the talk practises, up to 5 — the bot's `...` templates, counted in the
   * outcome's result (Free-Talk 033): "I'd like to ...", "Can I have ... / Could I have ...".
   */
  readonly structures?: readonly string[];
}

export interface FreeTalkCanDo {
  readonly text: string;
  /** The curriculum's code for it, free-form, up to 64; the bot keeps it as `content:<n>` when absent. */
  readonly ref?: string;
}

/** What a step asks the free-talk bot for: a catalog scenario by key, or the scenario itself. */
export type FreeTalkActivity = FreeTalkScenarioKey | FreeTalkScenario;

/** Whether the step sent the scenario itself (and not only its key). */
export const isFreeTalkContent = (activity: FreeTalkActivity): activity is FreeTalkScenario => 'title' in activity;

/** Where the step's placeholder sits inside the frame, in CSS pixels — for an inline placement. */
export interface ExternalBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * asset → host. `open` asks for the activity, `layout` moves its box, `close` takes it down.
 * `key` names the step, so the host's answer can say which one it is about.
 */
export interface ExternalPayload {
  readonly action: 'open' | 'layout' | 'close';
  readonly service: ExternalService;
  readonly key: string;
  /** Required with `open`: the document handed to the activity, unchanged. */
  readonly activity?: FreeTalkActivity;
  readonly box?: ExternalBox;
}

/** host → asset: where the activity is. `unavailable`: this host cannot open it, or will not now. */
export const EXTERNAL_STATUSES = ['open', 'finished', 'closed', 'unavailable'] as const;
export type ExternalStatus = (typeof EXTERNAL_STATUSES)[number];

export interface ExternalStatusPayload {
  readonly key: string;
  readonly status: ExternalStatus;
}

// ---------------------------------------------------------------- guards

const MAX_TEXT = 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const text = (value: unknown, max = MAX_TEXT): boolean =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max;
/** Only these keys, so nothing rides along with the content — no learner data, no URL, no token. */
const onlyKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean =>
  Object.keys(value).every((key) => keys.includes(key));

/** The bot's key: letters, digits, dots, hyphens and underscores — never a URL or a sentence. */
const SCENARIO_KEY = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
/** On the bot's screen only English is shown (its 011): no Hebrew, Arabic, Cyrillic or Ethiopic letter. */
// The bot's own ranges (its scenario_content.NON_ENGLISH): Hebrew and Arabic with their presentation
// forms, Cyrillic with its supplements and extensions, Ethiopic with its extensions.
const NOT_ENGLISH = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\uFB1D-\uFDFF\uFE70-\uFEFF\u0400-\u052F\u1C80-\u1C8F\u2DE0-\u2DFF\uA640-\uA69F\u1200-\u139F\u2D80-\u2DDF\uAB00-\uAB2F\u{1E7E0}-\u{1E7FF}]/u;
/** Nothing that names a person: a nine-digit run (an ID number), an email address. */
// As the bot reads it: nine digits standing alone (not part of a longer number or word), an email.
const IDENTITY = /(?<![0-9A-Za-z])\d{9}(?![0-9A-Za-z])|[^@\s]+@[^@\s]+\.[^@\s]+/;

const shown = (value: unknown, max: number): boolean =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max && !NOT_ENGLISH.test(value) && !IDENTITY.test(value);
const shownList = (value: unknown, min: number, max: number, each: number): boolean =>
  Array.isArray(value) && value.length >= min && value.length <= max && value.every((item) => shown(item, each)) &&
  // A repeat as the bot counts one: the same word ignoring case and the spaces around it.
  new Set(value.map((item) => String(item).trim().toLowerCase())).size === value.length;

/** A string's length in UTF-8 — counted here, so the contract needs no runtime's encoder. */
function utf8Bytes(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** A can-do code as the bot takes it (its 033): any text of 1–64 characters, no line breaks. */
const CAN_DO_REF = /^[^\r\n]{1,64}$/;

/** Up to 3 can-do statements (the bot's 027/033): English text up to 200, and an optional free-form code. */
const isCanDoList = (value: unknown): boolean =>
  Array.isArray(value) && value.length <= 3 &&
  value.every((entry) => isRecord(entry) && onlyKeys(entry, ['text', 'ref']) && shown(entry['text'], 200) &&
    (entry['ref'] === undefined || (typeof entry['ref'] === 'string' && CAN_DO_REF.test(entry['ref']) && entry['ref'].trim() !== '')));

/** Up to 5 sentence patterns (the bot's 033 `structures`): English, up to 120 each, no repeats. */
const isStructureList = (value: unknown): boolean => shownList(value, 0, 5, 120);

/** The bot's `scenario/1`, refused here as the bot would refuse it — so the asset learns at once. */
function isScenarioContent(value: Record<string, unknown>): boolean {
  if (!onlyKeys(value, ['scenarioId', 'type', 'title', 'level', 'character', 'openingLine', 'situation', 'goals', 'words', 'persona', 'canDo', 'structures'])) return false;
  const character = value['character'];
  const persona = value['persona'];
  const canDo = value['canDo'];
  const structures = value['structures'];
  return (
    SCENARIO_KEY.test(String(value['scenarioId'])) &&
    (FREE_TALK_TYPES as readonly unknown[]).includes(value['type']) &&
    shown(value['title'], 120) &&
    (FREE_TALK_LEVELS as readonly unknown[]).includes(value['level']) &&
    isRecord(character) && onlyKeys(character, ['name', 'role', 'voice']) &&
    shown(character['name'], 40) && shown(character['role'], 60) &&
    (FREE_TALK_VOICES as readonly unknown[]).includes(character['voice']) &&
    shown(value['openingLine'], 300) &&
    shown(value['situation'], 600) &&
    shownList(value['goals'], 1, 6, 160) &&
    shownList(value['words'], 0, 12, 40) &&
    (persona === undefined || (typeof persona === 'string' && persona.trim().length > 0 && persona.length <= 2000 && !IDENTITY.test(persona))) &&
    (canDo === undefined || isCanDoList(canDo)) &&
    (structures === undefined || isStructureList(structures)) &&
    utf8Bytes(JSON.stringify(value)) <= 8192
  );
}

export function isFreeTalkActivity(value: unknown): value is FreeTalkActivity {
  if (!isRecord(value) || typeof value['scenarioId'] !== 'string' || !SCENARIO_KEY.test(value['scenarioId'])) return false;
  // Exactly one of the two: the key alone, or the whole scenario.
  return Object.keys(value).length === 1 || isScenarioContent(value);
}

function isBox(value: unknown): value is ExternalBox {
  return (
    isRecord(value) &&
    ['x', 'y', 'width', 'height'].every((key) => typeof value[key] === 'number' && Number.isFinite(value[key])) &&
    (value['width'] as number) >= 0 &&
    (value['height'] as number) >= 0
  );
}

export function isExternalPayload(value: unknown): value is ExternalPayload {
  if (!isRecord(value) || !onlyKeys(value, ['action', 'service', 'key', 'activity', 'box'])) return false;
  const action = value['action'];
  if (action !== 'open' && action !== 'layout' && action !== 'close') return false;
  if (!(EXTERNAL_SERVICES as readonly unknown[]).includes(value['service'])) return false;
  if (!text(value['key'], 200)) return false;
  if (value['box'] !== undefined && !isBox(value['box'])) return false;
  if (action === 'open') return isFreeTalkActivity(value['activity']);
  return value['activity'] === undefined;
}

export function isExternalStatusPayload(value: unknown): value is ExternalStatusPayload {
  return (
    isRecord(value) &&
    text(value['key'], 200) &&
    (EXTERNAL_STATUSES as readonly unknown[]).includes(value['status'])
  );
}
